import { CalendarDays, LockKeyhole, RotateCcw } from "lucide-react";
import type {
  ContractPairSelection,
  MappingMode,
  PairDashboardFixture,
  PairId,
  QualityStatus,
} from "../features/dashboard/types";
import { QualityBadge } from "./QualityBadge";

interface FilterBarProps {
  readonly fixture: Pick<PairDashboardFixture, "manualLegAChoices" | "manualLegBChoices">;
  readonly selectedPairId: PairId;
  readonly mappingMode: MappingMode;
  readonly asOf: string;
  readonly minAsOf?: string;
  readonly maxAsOf: string;
  readonly activeContracts: ContractPairSelection | null;
  readonly manualContracts: ContractPairSelection | null;
  readonly qualityStatus: QualityStatus;
  readonly qualityLabel?: string;
  readonly onPairChange: (pairId: PairId) => void;
  readonly onMappingModeChange: (mode: MappingMode) => void;
  readonly onAsOfChange: (asOf: string) => void;
  readonly onManualLegAChange: (contractId: string) => void;
  readonly onManualLegBChange: (contractId: string) => void;
  readonly onReset: () => void;
}

const pairOptions: readonly { id: PairId; label: string }[] = [
  { id: "canola-matif-rapeseed", label: "MATIF Rapeseed − ICE Canola" },
  { id: "canola-soybean", label: "ICE Canola − CBOT Soybean" },
  { id: "canola-soybean-oil", label: "CBOT Soybean Oil − ICE Canola" },
];

export function FilterBar({
  fixture,
  selectedPairId,
  mappingMode,
  asOf,
  minAsOf,
  maxAsOf,
  activeContracts,
  manualContracts,
  qualityStatus,
  qualityLabel,
  onPairChange,
  onMappingModeChange,
  onAsOfChange,
  onManualLegAChange,
  onManualLegBChange,
  onReset,
}: FilterBarProps) {
  return (
    <section aria-label="Global analysis controls" className="rounded-xl border border-divider bg-raised/55 p-4 shadow-sm">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-[minmax(230px,1.2fr)_auto_minmax(170px,.7fr)_auto] xl:items-end">
        <label className="flex min-w-0 flex-col gap-1.5 text-xs font-bold uppercase tracking-wider text-muted">
          Pair preset
          <select
            aria-label="Pair preset"
            value={selectedPairId}
            onChange={(event) => onPairChange(event.target.value as PairId)}
            className="control-field"
          >
            {pairOptions.map((pair) => <option key={pair.id} value={pair.id}>{pair.label}</option>)}
          </select>
        </label>

        <fieldset>
          <legend className="mb-1.5 text-xs font-bold uppercase tracking-wider text-muted">Mapping mode</legend>
          <div className="grid h-10 grid-cols-2 rounded-lg border border-divider bg-inset p-1" role="group" aria-label="Mapping mode">
            {(["auto", "manual"] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                aria-pressed={mappingMode === mode}
                disabled={mode === "manual" && manualContracts === null}
                onClick={() => onMappingModeChange(mode)}
                className={`rounded-md px-4 text-xs font-bold capitalize transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:cursor-not-allowed disabled:opacity-40 ${
                  mappingMode === mode ? "bg-accent text-inset shadow-sm" : "text-muted hover:bg-raised hover:text-primary"
                }`}
              >
                {mode}
              </button>
            ))}
          </div>
        </fieldset>

        <label className="flex flex-col gap-1.5 text-xs font-bold uppercase tracking-wider text-muted">
          As-of date
          <span className="relative">
            <CalendarDays aria-hidden="true" className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-faint" />
            <input
              aria-label="As-of date"
              type="date"
              min={minAsOf}
              max={maxAsOf}
              value={asOf}
              onChange={(event) => onAsOfChange(event.target.value)}
              className="control-field pl-9"
            />
          </span>
        </label>

        <button type="button" onClick={onReset} className="secondary-button h-10 xl:self-end">
          <RotateCcw aria-hidden="true" className="h-4 w-4" />
          Reset
        </button>
      </div>

      <div className="mt-4 border-t border-divider-subtle pt-4">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
          <div className="grid min-w-0 flex-1 grid-cols-1 gap-3 lg:grid-cols-2">
            {mappingMode === "manual" ? (
              <>
                <label className="flex min-w-0 flex-col gap-1.5 text-xs font-bold uppercase tracking-wider text-muted">
                  Leg A contract
                  <select aria-label="Leg A contract" className="control-field" value={manualContracts?.legA.id ?? ""} onChange={(event) => onManualLegAChange(event.target.value)}>
                    {fixture.manualLegAChoices.map((contract) => (
                      <option key={contract.id} value={contract.id}>{contract.label}</option>
                    ))}
                  </select>
                </label>
                <label className="flex min-w-0 flex-col gap-1.5 text-xs font-bold uppercase tracking-wider text-muted">
                  Leg B contract
                  <select aria-label="Leg B contract" className="control-field" value={manualContracts?.legB.id ?? ""} onChange={(event) => onManualLegBChange(event.target.value)}>
                    {fixture.manualLegBChoices.map((contract) => (
                      <option key={contract.id} value={contract.id}>{contract.label}</option>
                    ))}
                  </select>
                </label>
              </>
            ) : (
              <>
                <ContractReadout label="Leg A · Auto" contractLabel={activeContracts?.legA.label ?? "N/A for this date"} />
                <ContractReadout label="Leg B · Auto" contractLabel={activeContracts?.legB.label ?? "N/A for this date"} />
              </>
            )}
          </div>

          <div className="flex min-w-0 flex-col gap-2 rounded-lg border border-accent/25 bg-accent/8 px-3 py-2.5 xl:min-w-[310px]">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted">Active pair</span>
              <QualityBadge status={qualityStatus} label={qualityLabel} />
            </div>
            <p className="truncate text-sm font-bold text-primary" data-testid="active-contract-pair">
              {activeContracts ? `${activeContracts.legA.compact} / ${activeContracts.legB.compact}` : "N/A"}
            </p>
            <p className="text-[10px] leading-relaxed text-secondary">
              {activeContracts
                ? <>{activeContracts.legA.label} <span aria-hidden="true">↔</span> {activeContracts.legB.label}</>
                : "No validated contract pair is available for the selected date."}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContractReadout({ label, contractLabel }: { readonly label: string; readonly contractLabel: string }) {
  return (
    <div className="rounded-lg border border-divider bg-inset/70 px-3 py-2.5">
      <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-faint">
        <LockKeyhole aria-hidden="true" className="h-3.5 w-3.5" />
        {label}
      </p>
      <p className="mt-1 truncate text-sm font-semibold text-primary">{contractLabel}</p>
    </div>
  );
}
